import React, { useState } from 'react';
import { Box, Paper, ToggleButtonGroup, ToggleButton, Typography, Button, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import NetworkLayer from './NetworkLayer';
import ConnectionLines from './ConnectionLines';
import ControlPanel from './ControlPanel';
import TargetValueDisplay from './TargetValueDisplay';
import { useLanguage } from '../utils/languageContext';
import { 
  goodWeights, 
  badWeights, 
  forwardPropagation, 
  backwardPropagation,
  checkAndFixWeights 
} from '../utils/neuralNetworkUtils';

const NetworkContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  position: 'relative',
  minHeight: '400px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '12px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
  background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
  overflow: 'hidden',
  width: '100%',
}));

// 创建一个样式化的按钮组件
const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0, 1),
  padding: theme.spacing(1, 3),
  boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 14px rgba(0,0,0,0.15)',
  },
}));

const NeuralNetworkSimulator = () => {
  // Get translation function
  const { t } = useLanguage();
  
  // Neural network structure
  const inputSize = 2;
  const hiddenSize = 3;
  const outputSize = 1;

  // Mode state - "train" for training mode, "apply" for application mode
  const [mode, setMode] = useState("train");

  // State
  const [weights, setWeights] = useState({
    W1: [
      [0.5, 0.4],
      [0.1, 0.2],
      [0.3, 0.6]
    ],
    W2: [
      [0.1, 0.4, 0.7]
    ]
  });
  
  const [inputs, setInputs] = useState([1, 2]);
  const [layerOutputs, setLayerOutputs] = useState({
    A1: [0, 0, 0],
    A2: [0]
  });
  const [visibleLayers, setVisibleLayers] = useState({
    input: true,
    hidden: true,
    output: true
  });
  const [animationPhase, setAnimationPhase] = useState(null);
  const [oldWeights, setOldWeights] = useState(null);
  const [animationStep, setAnimationStep] = useState(0);
  const [visibleNodes, setVisibleNodes] = useState({
    hidden: [false, false, false],
    output: [false]
  });
  const [visibleConnections, setVisibleConnections] = useState({
    inputToHidden: [[false, false], [false, false], [false, false]],
    hiddenToOutput: [[false, false, false]]
  });
  const [learningRate, setLearningRate] = useState(0.1);
  const [targetValue, setTargetValue] = useState(3.0);
  const [forwardDisabled, setForwardDisabled] = useState(false);
  const [backwardDisabled, setBackwardDisabled] = useState(true);
  
  // Handle input changes
  const handleInput1Change = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setInputs(prevInputs => [value, prevInputs[1]]);
  };
  
  const handleInput2Change = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setInputs(prevInputs => [prevInputs[0], value]);
  };
  
  const handleLearningRateChange = (_, value) => {
    setLearningRate(value);
  };
  
  const handleTargetValueChange = (e) => {
    const value = parseFloat(e.target.value) || 0;
    setTargetValue(value);
  };
  
  // Handle mode change
  const handleModeChange = (_, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
      
      // Reset network state when changing modes
      resetState();
    }
  };
  
  // Forward propagation
  const handleForwardPropagation = () => {
    // Safety check for NaN in weights before propagation
    const safeWeights = checkAndFixWeights(weights);
    if (safeWeights !== weights) {
      setWeights(safeWeights);
    }
    
    // Calculate forward propagation results
    const { A1, A2 } = forwardPropagation(inputs, weights);
    
    // Create step outputs for each animation phase
    const stepOutputs = [
      { A1: [0, 0, 0], A2: [0] },  // Initial state
      { A1: [A1[0], 0, 0], A2: [0] },  // Show first hidden node
      { A1: [A1[0], A1[1], 0], A2: [0] },  // Show second hidden node
      { A1: [A1[0], A1[1], A1[2]], A2: [0] },  // Show third hidden node
      { A1: [A1[0], A1[1], A1[2]], A2: [A2[0]] }  // Show output node
    ];
    
    // Reset animation state
    setAnimationStep(0);
    
    // 新的逻辑：设置所有神经元为可见，只是其值从空开始
    setVisibleNodes({
      hidden: [true, true, true], // 所有隐藏层神经元都可见
      output: [true]              // 输出层神经元也可见
    });
    
    setVisibleConnections({
      inputToHidden: [[false, false], [false, false], [false, false]],
      hiddenToOutput: [[false, false, false]]
    });
    
    // Set initial layer outputs to first step
    setLayerOutputs(stepOutputs[0]);
    
    // Make sure all layers are visible
    setVisibleLayers({ input: true, hidden: true, output: true });
    
    // Delay between animation steps
    const stepDelay = 800;
    
    // Start animation sequence
    const startAnimation = () => {
      // Step 1: Show connections from input to first hidden node
      setAnimationPhase('forward-input-to-hidden-1');
      setVisibleConnections({
        inputToHidden: [[true, true], [false, false], [false, false]],
        hiddenToOutput: [[false, false, false]]
      });
      
      // Step 2: Show first hidden node value
      setTimeout(() => {
        setAnimationStep(1);
        setLayerOutputs(stepOutputs[1]);
        
        // Step 3: Show connections from input to second hidden node
        setTimeout(() => {
          setAnimationPhase('forward-input-to-hidden-2');
          setVisibleConnections({
            inputToHidden: [[true, true], [true, true], [false, false]],
            hiddenToOutput: [[false, false, false]]
          });
          
          // Step 4: Show second hidden node value
          setTimeout(() => {
            setAnimationStep(2);
            setLayerOutputs(stepOutputs[2]);
            
            // Step 5: Show connections from input to third hidden node
            setTimeout(() => {
              setAnimationPhase('forward-input-to-hidden-3');
              setVisibleConnections({
                inputToHidden: [[true, true], [true, true], [true, true]],
                hiddenToOutput: [[false, false, false]]
              });
              
              // Step 6: Show third hidden node value
              setTimeout(() => {
                setAnimationStep(3);
                setLayerOutputs(stepOutputs[3]);
                
                // Step 7: Show connections from hidden to output
                setTimeout(() => {
                  setAnimationPhase('forward-hidden-to-output');
                  setVisibleConnections({
                    inputToHidden: [[true, true], [true, true], [true, true]],
                    hiddenToOutput: [[true, true, true]]
                  });
                  
                  // Step 8: Show output node value
                  setTimeout(() => {
                    setAnimationStep(4);
                    setLayerOutputs(stepOutputs[4]);
                    
                    // Final step: Complete animation
                    setTimeout(() => {
                      setAnimationPhase(null);
                      setForwardDisabled(true);
                      setBackwardDisabled(false);
                    }, stepDelay);
                  }, stepDelay);
                }, stepDelay);
              }, stepDelay);
            }, stepDelay);
          }, stepDelay);
        }, stepDelay);
      }, stepDelay);
    };
    
    // Start the animation
    startAnimation();
  };
  
  // Backward propagation
  const handleBackwardPropagation = () => {
    // Safety check for NaN in weights before propagation
    const safeWeights = checkAndFixWeights(weights);
    if (safeWeights !== weights) {
      setWeights(safeWeights);
    }
    
    // Save original weights for animation
    setOldWeights({
      W1: weights.W1.map(row => [...row]),
      W2: weights.W2.map(row => [...row])
    });
    
    // 计算反向传播
    const { newW1, newW2 } = backwardPropagation(
      inputs,
      layerOutputs.A1,
      layerOutputs.A2,
      weights.W1,
      weights.W2,
      [targetValue],
      learningRate
    );
    
    // 实现分阶段反向传播动画
    
    // 第一阶段：输出层到隐藏层（W2权重更新）
    setAnimationPhase('backward-phase1');
    
    // 创建视觉提示
    const statusMessage = document.createElement('div');
    statusMessage.style.position = 'fixed';
    statusMessage.style.top = '10px';
    statusMessage.style.left = '50%';
    statusMessage.style.transform = 'translateX(-50%)';
    statusMessage.style.backgroundColor = 'rgba(255, 0, 0, 0.8)';
    statusMessage.style.color = 'white';
    statusMessage.style.padding = '10px 20px';
    statusMessage.style.borderRadius = '5px';
    statusMessage.style.fontWeight = 'bold';
    statusMessage.style.fontSize = '18px';
    statusMessage.style.zIndex = '1000';
    statusMessage.style.transition = 'opacity 0.5s';
    statusMessage.id = 'phase-status';
    statusMessage.textContent = t('backpropagationPhase1'); // "第一阶段：输出层到隐藏层的反向传播"
    document.body.appendChild(statusMessage);
    
    // 加快动画速度50%：将2秒延迟改为1秒
    setTimeout(() => {
      // 只更新W2权重
      setWeights({
        W1: weights.W1, // 保持W1不变
        W2: newW2       // 只更新W2
      });
      
      // 加快动画速度50%：将5秒延迟改为2.5秒
      setTimeout(() => {
        // 更新视觉提示
        const phaseStatus = document.getElementById('phase-status');
        if (phaseStatus) {
          phaseStatus.textContent = t('backpropagationPhase2'); // "第二阶段：隐藏层到输入层的反向传播"
        }
        
        // 设置第二阶段动画状态
        setAnimationPhase('backward-phase2');
        
        // 保存W2的新值和W1的原始值
        setOldWeights({
          W1: weights.W1.map(row => [...row]),
          W2: newW2.map(row => [...row])
        });
        
        // 加快动画速度50%：将2秒延迟改为1秒
        setTimeout(() => {
          // 完成所有权重更新
          setWeights({
            W1: newW1,
            W2: newW2
          });
          
          // 加快动画速度50%：将5秒延迟改为2.5秒
          setTimeout(() => {
            // 移除视觉提示
            const phaseStatus = document.getElementById('phase-status');
            if (phaseStatus) {
              phaseStatus.style.opacity = '0';
              setTimeout(() => phaseStatus.remove(), 500);
            }
            
            // 结束动画阶段
            setAnimationPhase(null);
            setOldWeights(null);
            
            // 重新启用前向传播按钮，禁用反向传播按钮
            setForwardDisabled(false);
            setBackwardDisabled(true);
          }, 2500);
        }, 1000);
      }, 2500);
    }, 1000);
  };
  
  // Apply network function for apply mode
  const handleApplyNetwork = () => {
    // Safety check for NaN in weights before propagation
    const safeWeights = checkAndFixWeights(weights);
    if (safeWeights !== weights) {
      setWeights(safeWeights);
    }
    
    // Calculate output but don't display immediately
    const { A1, A2 } = forwardPropagation(inputs, weights);
    
    // Create step outputs for each animation phase
    const stepOutputs = [
      { A1: [0, 0, 0], A2: [0] },  // Initial state
      { A1: [A1[0], 0, 0], A2: [0] },  // Show first hidden node
      { A1: [A1[0], A1[1], 0], A2: [0] },  // Show second hidden node
      { A1: [A1[0], A1[1], A1[2]], A2: [0] },  // Show third hidden node
      { A1: [A1[0], A1[1], A1[2]], A2: [A2[0]] }  // Show output node
    ];
    
    // Reset animation state
    setAnimationStep(0);
    
    // 确保所有神经元始终可见
    setVisibleNodes({
      hidden: [true, true, true],
      output: [true]
    });
    
    setVisibleConnections({
      inputToHidden: [[false, false], [false, false], [false, false]],
      hiddenToOutput: [[false, false, false]]
    });
    
    // Set initial layer outputs to first step
    setLayerOutputs(stepOutputs[0]);
    
    // 确保所有层始终可见
    setVisibleLayers({ input: true, hidden: true, output: true });
    
    // Delay between animation steps
    const stepDelay = 800;
    
    // Start animation sequence - same as forward propagation
    const startAnimation = () => {
      // Step 1: Show connections from input to first hidden node
      setAnimationPhase('forward-input-to-hidden-1');
      setVisibleConnections({
        inputToHidden: [[true, true], [false, false], [false, false]],
        hiddenToOutput: [[false, false, false]]
      });
      
      // Step 2: Show first hidden node value
      setTimeout(() => {
        setAnimationStep(1);
        setLayerOutputs(stepOutputs[1]);
        
        // Step 3: Show connections from input to second hidden node
        setTimeout(() => {
          setAnimationPhase('forward-input-to-hidden-2');
          setVisibleConnections({
            inputToHidden: [[true, true], [true, true], [false, false]],
            hiddenToOutput: [[false, false, false]]
          });
          
          // Step 4: Show second hidden node value
          setTimeout(() => {
            setAnimationStep(2);
            setLayerOutputs(stepOutputs[2]);
            
            // Step 5: Show connections from input to third hidden node
            setTimeout(() => {
              setAnimationPhase('forward-input-to-hidden-3');
              setVisibleConnections({
                inputToHidden: [[true, true], [true, true], [true, true]],
                hiddenToOutput: [[false, false, false]]
              });
              
              // Step 6: Show third hidden node value
              setTimeout(() => {
                setAnimationStep(3);
                setLayerOutputs(stepOutputs[3]);
                
                // Step 7: Show connections from hidden to output
                setTimeout(() => {
                  setAnimationPhase('forward-hidden-to-output');
                  setVisibleConnections({
                    inputToHidden: [[true, true], [true, true], [true, true]],
                    hiddenToOutput: [[true, true, true]]
                  });
                  
                  // Step 8: Show output node value
                  setTimeout(() => {
                    setAnimationStep(4);
                    setLayerOutputs(stepOutputs[4]);
                    
                    // Final step: Complete animation
                    setTimeout(() => {
                      setAnimationPhase(null);
                    }, stepDelay);
                  }, stepDelay);
                }, stepDelay);
              }, stepDelay);
            }, stepDelay);
          }, stepDelay);
        }, stepDelay);
      }, stepDelay);
    };
    
    // Start the animation
    startAnimation();
  };
  
  // Set good initialization weights
  const handleGoodInitialization = () => {
    setWeights(goodWeights);
    resetState();
  };
  
  // Set bad initialization weights
  const handleBadInitialization = () => {
    setWeights(badWeights);
    resetState();
  };
  
  // Reset state
  const resetState = () => {
    // 确保无论在什么模式下，所有层都始终可见
    setVisibleLayers({ input: true, hidden: true, output: true });
    
    // 清空层输出
    setLayerOutputs({ A1: [0, 0, 0], A2: [0] });
    
    // 确保所有节点都可见
    setVisibleNodes({
      hidden: [true, true, true],
      output: [true]
    });
    
    // 重置连接的可见性（可以选择是否也显示它们）
    setVisibleConnections({
      inputToHidden: [[false, false], [false, false], [false, false]],
      hiddenToOutput: [[false, false, false]]
    });
    
    // Reset animation phase
    setAnimationPhase(null);
    // Enable forward propagation, disable backpropagation
    setForwardDisabled(false);
    setBackwardDisabled(true);
  };
  
  return (
    <Box sx={{ mb: 8 }}>
      {/* Mode toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <ToggleButtonGroup
          color="primary"
          value={mode}
          exclusive
          onChange={handleModeChange}
          aria-label="Mode Selection"
        >
          <ToggleButton value="train" aria-label="Training Mode">
            {t('trainingMode')}
          </ToggleButton>
          <ToggleButton value="apply" aria-label="Apply Mode">
            {t('applyMode')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <NetworkContainer>
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            width: '100%', 
            position: 'relative',
            height: '450px', // 增加高度以容纳按钮
            padding: '0 40px'
          }}
        >
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-evenly',
              width: '100%', 
              position: 'relative',
              flex: 1,
              padding: '0 40px'
            }}
          >
            <ConnectionLines 
              W1={weights.W1} 
              W2={weights.W2} 
              oldW1={oldWeights?.W1}
              oldW2={oldWeights?.W2}
              inputSize={inputSize} 
              hiddenSize={hiddenSize} 
              outputSize={outputSize}
              animationPhase={animationPhase}
              visibleConnections={visibleConnections}
            />
            
            <NetworkLayer 
              layerIndex={0} 
              layerName={t('inputLayer')} 
              size={inputSize} 
              values={inputs} 
              visible={visibleLayers.input} 
            />
            
            <NetworkLayer 
              layerIndex={1} 
              layerName={t('hiddenLayer')} 
              size={hiddenSize} 
              values={layerOutputs.A1} 
              visible={visibleLayers.hidden}
              visibleNodes={visibleNodes.hidden}
              animationStep={animationStep}
            />
            
            <NetworkLayer 
              layerIndex={2} 
              layerName={t('outputLayer')} 
              size={outputSize} 
              values={layerOutputs.A2} 
              visible={visibleLayers.output}
              visibleNodes={visibleNodes.output}
              animationStep={animationStep}
            />
            
            <TargetValueDisplay targetValue={targetValue} mode={mode} />
          </Box>
          
          {/* 添加分隔线 */}
          <Divider sx={{ my: 1.5, width: '100%' }} />
          
          {/* 添加按钮到神经网络图内 */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
            {mode === "train" ? (
              <>
                <StyledButton 
                  variant="contained" 
                  color="primary" 
                  onClick={handleForwardPropagation}
                  disabled={forwardDisabled}
                  sx={{ fontSize: '16px' }}
                >
                  {t('forwardPropagation')}
                </StyledButton>
                <StyledButton 
                  variant="contained" 
                  color="secondary" 
                  onClick={handleBackwardPropagation}
                  disabled={backwardDisabled}
                  sx={{ fontSize: '16px' }}
                >
                  {t('backwardPropagation')}
                </StyledButton>
              </>
            ) : (
              <StyledButton 
                variant="contained" 
                color="primary" 
                onClick={handleApplyNetwork}
                sx={{ fontSize: '16px', px: 5 }}
              >
                {t('applyNetwork')}
              </StyledButton>
            )}
          </Box>
        </Box>
      </NetworkContainer>
      
      <ControlPanel 
        input1={inputs[0]}
        input2={inputs[1]}
        learningRate={learningRate}
        targetValue={targetValue}
        onInput1Change={handleInput1Change}
        onInput2Change={handleInput2Change}
        onLearningRateChange={handleLearningRateChange}
        onTargetValueChange={handleTargetValueChange}
        onGoodInitialization={handleGoodInitialization}
        onBadInitialization={handleBadInitialization}
        mode={mode}
        outputValue={layerOutputs.A2[0] || 0}
      />
    </Box>
  );
};

export default NeuralNetworkSimulator; 